import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import Loader from "../../components/Loader";
import CustomAvatarUploader from "../../components/profile/CustomAvatarUploader";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { apiClient, toJsonFile, useApiQuery } from "../../services/api";
import { defaultAvatarImages } from "../../domains/user/utils/pickUserImage";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

export default function RequestCertificationPage() {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [customAvatarFile, setCustomAvatarFile] = useState<string>("");
  const [requireUpload, setRequireUpload] = useState<boolean | null>(null);
  const [willNewImageUpload, setWillNewImageUpload] = useState(false);

  const certifiedQuery = useApiQuery(
    "/webapp/private/lemon-certified/request",
    currentUser !== null && currentUser.lemonCertified !== true
      ? { durable: true }
      : false
  );

  // Redirect if already certified or if existing request found
  const certifiedBody =
    certifiedQuery.state === "success" ? certifiedQuery.body : undefined;
  useEffect(() => {
    if (currentUser?.lemonCertified === true) {
      displayToast(t("Something went wrong. Please try again."), "error");
      history.replace("/settings");
      return;
    }
    if (certifiedBody?.status !== undefined) {
      history.replace("/settings");
    }
  }, [currentUser?.lemonCertified, certifiedBody, history, t]);

  // Init avatar state once query succeeds and requireUpload hasn't been set
  useEffect(() => {
    if (
      certifiedQuery.state !== "success" ||
      requireUpload !== null ||
      currentUser === null
    )
      return;
    if (
      currentUser.profilePicture?.image?.url !== undefined &&
      currentUser.profilePicture.image.url !== ""
    ) {
      setCustomAvatarFile(currentUser.profilePicture.image.url);
      setRequireUpload(false);
    } else {
      setRequireUpload(true);
    }
  }, [certifiedQuery.state, requireUpload, currentUser]);

  function handleChangeAvatarCustom(url: string) {
    setCustomAvatarFile(url);
    setRequireUpload(false);
    setWillNewImageUpload(true);
  }

  async function handleSubmitImage(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (willNewImageUpload) {
        const response = await fetch(customAvatarFile);
        const blob = await response.blob();
        const file = new File([blob], "avatar", { type: "image/jpeg" });

        try {
          await apiClient.post("/webapp/private/users/me/avatar", {
            json: { file: await toJsonFile(file), pictureName: null },
          });
          displayToast(t("Profile picture updated"), "success");
          await reloadCurrentUser();
          handleSubmitCertification();
        } catch {
          displayToast(t("Something went wrong. Please try again."), "error");
        }
      } else {
        handleSubmitCertification();
      }
    } catch (error) {
      console.error("Couldn't upload profile picture.", error);
    }
  }

  async function handleSubmitCertification(e?: React.FormEvent) {
    e?.preventDefault();

    try {
      await apiClient.post("/webapp/private/lemon-certified/request");
      displayToast(t("Certification request sent"), "success");
      history.replace("/settings");
    } catch {
      // api error already captured by onRequestError.
    }
  }

  // Inside the page's own chrome, so the header's back control stays reachable.
  if (certifiedQuery.state === "error") {
    return (
      <ScrollableLayout
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <ContentHeader transparent title={t("Lemon certified request")} />
        }
      >
        <p className="px-5 py-6 text-ui-body text-ds-slate-600">
          {certifiedQuery.message}
        </p>
      </ScrollableLayout>
    );
  }
  if (
    requireUpload === null ||
    certifiedQuery.state !== "success" ||
    !currentUser
  ) {
    return <Loader expanded />;
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader transparent title={t("Lemon certified request")} />
      }
    >
      <div className="m-0 flex-1 flex flex-col items-center justify-center gap-[22px] p-[22px]">
        <CustomAvatarUploader
          avatarUrl={
            customAvatarFile
              ? customAvatarFile
              : currentUser.profilePicture?.name !== undefined &&
                  currentUser.profilePicture.name !== null
                ? defaultAvatarImages[currentUser.profilePicture.name].default
                : ""
          }
          fullname={currentUser.fullname}
          buttonText={
            customAvatarFile !== "" ||
            currentUser.profilePicture?.image?.url !== undefined
              ? t("Choose another picture")
              : t("Upload profile picture")
          }
          onChange={handleChangeAvatarCustom}
        />
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={requireUpload}
          onClick={
            willNewImageUpload ? handleSubmitImage : handleSubmitCertification
          }
        >
          {t("Request Certification")}
        </Button>
      </div>
    </ScrollableLayout>
  );
}
